/**
 * Aufbau der Netzwerk-Ansicht (Design-System §6).
 *
 * Kern-Node in der Mitte, darum Bereichs-Nodes (aus `apps.area`) und
 * Standalone-Nodes (eigener Node direkt am Kern). Alles rein aus den Daten –
 * hier stehen nur Gruppierung, Farbzuordnung und Positionen, damit das ohne
 * DOM testbar bleibt.
 */

export type Accent = 'pink' | 'teal' | 'cyan' | 'purple' | 'magenta';

export interface NetworkApp {
  id: string;
  slug: string;
  name: string;
  url: string;
  icon: string | null;
  sort_order: number;
  area: string;
  /** true = sichtbar, aber noch ohne Ziel. */
  coming_soon?: boolean;
}

export type NodeKind = 'area' | 'standalone' | 'ghost';

export interface NetworkNode {
  key: string;
  kind: NodeKind;
  /** Press-Start-2P-Label, uppercase. */
  label: string;
  /** Space-Mono-Zeile darunter. */
  sub: string;
  accent: Accent;
  icon: string;
  /** Nur bei kind 'area': die App-Sub-Nodes. */
  apps: NetworkApp[];
  /** Nur bei kind 'standalone': Ziel beim Tap. */
  url?: string;
  external?: boolean;
  /** Zähler am Node. Phase 3: statisch 0. */
  count: number;
}

const BASE_DOMAIN = 'smiling-data-club.de';

/** Bekannte Bereiche: Label und Akzentfarbe. Neue Bereiche werden lila (Design-System §1). */
const AREA_META: Record<string, { label: string; accent: Accent; icon: string }> = {
  hr: { label: 'HR', accent: 'pink', icon: 'people' },
  sales: { label: 'SALES', accent: 'teal', icon: 'chart' },
  service: { label: 'SERVICE', accent: 'cyan', icon: 'service' },
  tools: { label: 'TOOLS', accent: 'purple', icon: 'tools' },
  // Magenta ist ausschließlich Bereichsfarbe, nie Zustand (Design-System §1).
  marketing: { label: 'MARKETING', accent: 'magenta', icon: 'grid' },
};

/** Standalone-Nodes wechseln sich farblich ab – im Mockup DocuWare cyan, Cube lila. */
const STANDALONE_ACCENTS: Accent[] = ['cyan', 'purple'];

export function isExternalUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return !(host === BASE_DOMAIN || host.endsWith(`.${BASE_DOMAIN}`));
  } catch {
    return false;
  }
}

function areaMeta(area: string): { label: string; accent: Accent; icon: string } {
  return AREA_META[area] ?? { label: area.toUpperCase(), accent: 'purple', icon: 'grid' };
}

export interface BuildOptions {
  /** Ghost-Node ist nur für Admins interaktiv. */
  isAdmin?: boolean;
}

/**
 * Baut die Node-Liste. Reihenfolge bestimmt die Position:
 * Bereiche (nach kleinster sort_order), dann Standalone (nach sort_order),
 * dann der Ghost-Node.
 */
export function buildNetwork(apps: NetworkApp[], _options: BuildOptions = {}): NetworkNode[] {
  const byArea = new Map<string, NetworkApp[]>();
  const standalone: NetworkApp[] = [];

  for (const app of [...apps].sort((a, b) => a.sort_order - b.sort_order)) {
    const area = app.area?.trim() || 'standalone';
    if (area === 'standalone') {
      standalone.push(app);
      continue;
    }
    const list = byArea.get(area) ?? [];
    list.push(app);
    byArea.set(area, list);
  }

  const nodes: NetworkNode[] = [];

  for (const [area, list] of byArea) {
    const meta = areaMeta(area);
    nodes.push({
      key: `area:${area}`,
      kind: 'area',
      label: meta.label,
      sub: `${list.length} ${list.length === 1 ? 'app' : 'apps'} · 0 offen`,
      accent: meta.accent,
      icon: meta.icon,
      apps: list,
      count: 0,
    });
  }

  standalone.forEach((app, index) => {
    const external = isExternalUrl(app.url);
    nodes.push({
      key: `app:${app.slug}`,
      kind: 'standalone',
      label: app.name.toUpperCase(),
      sub: external ? 'extern' : 'standalone',
      accent: STANDALONE_ACCENTS[index % STANDALONE_ACCENTS.length] ?? 'purple',
      icon: app.icon ?? 'grid',
      apps: [],
      url: app.url,
      external,
      count: 0,
    });
  });

  nodes.push({
    key: 'ghost',
    kind: 'ghost',
    label: 'NEU',
    sub: 'z. B. Operations',
    accent: 'purple',
    icon: 'plus',
    apps: [],
    count: 0,
  });

  return nodes;
}

// ---------------------------------------------------------------- Positionen

export interface Point {
  /** Prozent der Stage-Breite. */
  x: number;
  /** Prozent der Stage-Höhe. */
  y: number;
}

export const CORE_POSITION: Record<'desktop' | 'mobile', Point> = {
  desktop: { x: 50, y: 40 },
  mobile: { x: 50, y: 19 },
};

/**
 * Nutzbares Band für Nodes, in Prozent der Stage-Höhe.
 *
 * Unten sitzen jetzt die beiden Homescreen-Panels plus der Schwarze-Brett-Strip
 * (`.dock`), oben die Topbar. Nodes müssen dazwischen bleiben – sonst
 * verschwinden sie hinter den Panels, was auf dem Handy sofort auffällt.
 */
export const NETWORK_BAND: Record<'desktop' | 'mobile', { top: number; bottom: number }> = {
  desktop: { top: 16, bottom: 74 },
  mobile: { top: 36, bottom: 68 },
};

/**
 * Feste Plätze aus dem Mockup für den Desktop, bis fünf Nodes.
 * Der unterste Platz sitzt bei 72 statt 80 – darunter beginnt der Dock-Bereich.
 */
const SLOTS_DESKTOP: Point[] = [
  { x: 22, y: 24 },
  { x: 78, y: 26 },
  { x: 18, y: 64 },
  { x: 82, y: 68 },
  { x: 50, y: 72 },
];

const RING_RADIUS = { x: 32, y: 24 };

/** Ab so vielen Nodes wird es auf dem Handy dreispaltig und die Discs kleiner. */
export const DENSE_THRESHOLD = 6;

/** Spalten des mobilen Rasters: zwei normal, drei ab {@link DENSE_THRESHOLD}. */
export function mobileColumns(total: number): number {
  return total > DENSE_THRESHOLD ? 3 : 2;
}

/** Kleinere Discs, sobald das mobile Raster dreispaltig wird. */
export function isDense(total: number, mobile: boolean): boolean {
  return mobile && total > DENSE_THRESHOLD;
}

function ringPosition(index: number, total: number): Point {
  const core = CORE_POSITION.desktop;
  // Start oben, im Uhrzeigersinn.
  const angle = -Math.PI / 2 + (index / total) * Math.PI * 2;
  return {
    x: Math.round((core.x + Math.cos(angle) * RING_RADIUS.x) * 10) / 10,
    y: Math.round((core.y + Math.sin(angle) * RING_RADIUS.y) * 10) / 10,
  };
}

/**
 * Mobiles Raster unter dem Kern (Design-System §6: „Kern oben, Bereiche als
 * Spalte darunter"). Ein Ring wäre hier falsch – der Kern sitzt oben, ein Ring
 * um ihn würde die Hälfte der Nodes über den Bildschirmrand schieben.
 *
 * Zeilenabstand ergibt sich aus dem Band, damit auch neun Nodes hineinpassen.
 */
function gridPosition(index: number, total: number): Point {
  const band = NETWORK_BAND.mobile;
  const cols = mobileColumns(total);
  const rows = Math.max(1, Math.ceil(total / cols));
  const row = Math.floor(index / cols);

  const rowStart = row * cols;
  const inRow = Math.min(cols, total - rowStart);
  const col = index - rowStart;

  const y = rows === 1 ? band.top : band.top + (row * (band.bottom - band.top)) / (rows - 1);
  const spread = cols === 3 ? 30 : 23;
  const x = inRow === 1 ? 50 : 50 + (-1 + (2 * col) / (inRow - 1)) * spread;

  return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
}

/**
 * Position eines Nodes.
 *
 * Desktop: bis fünf Nodes die Mockup-Plätze, darüber ein Ring um den Kern.
 * Mobil: immer das Raster unter dem Kern.
 */
export function nodePosition(index: number, total: number, mobile: boolean): Point {
  if (mobile) return gridPosition(index, total);
  if (total <= SLOTS_DESKTOP.length) {
    return SLOTS_DESKTOP[index] ?? SLOTS_DESKTOP[SLOTS_DESKTOP.length - 1]!;
  }
  return ringPosition(index, total);
}

/**
 * Grösste Zahl von Sub-Nodes, die in einer Reihe unter dem Bereich noch Platz
 * hat. Darüber überlappen die Beschriftungen – bei vier Apps lagen sie mit der
 * alten Reihe nur ~8,7 Einheiten auseinander.
 */
const SUB_ROW_MAX = 3;

/** Basisradien des Sub-Node-Rings. Ellipse, weil die Bühne breiter als hoch ist. */
const SUB_RING_RADIUS = {
  desktop: { x: 17, y: 14 },
  mobile: { x: 19, y: 13 },
} as const;

/**
 * Mindestabstand zweier Sub-Nodes in Bühnen-Einheiten. Ein Node ist mit Label
 * rund 12 Einheiten breit; darunter überlappen die Namen. Wird vom Test
 * mitbenutzt, damit Wert und Prüfung nicht auseinanderlaufen.
 */
export const MIN_SUB_DISTANCE = 12;

/**
 * Plätze der App-Sub-Nodes um ihren Bereichs-Node.
 *
 * Bis drei Apps: eine Reihe darunter, wie im Mockup. Ab vier: ein **Ring** rund
 * um den Bereich, beginnend unten und im Uhrzeigersinn – in einer Reihe wäre der
 * Abstand zu klein und die Namen würden sich überlappen.
 *
 * Der Ring ist gefahrlos, weil Sub-Nodes nur im aufgezoomten Zustand
 * erscheinen: die Kamera steht dann auf dem Bereich, und die Nachbarbereiche
 * sind aus dem Bild gefahren.
 */
export function subNodePositions(parent: Point, count: number, mobile: boolean): Point[] {
  if (count === 0) return [];

  if (count <= SUB_ROW_MAX) {
    const spreadX = mobile ? 16 : 13;
    const dy = mobile ? 13 : 15;
    return Array.from({ length: count }, (_, i) => {
      const t = count === 1 ? 0 : -1 + (2 * i) / (count - 1);
      return { x: parent.x + t * spreadX, y: parent.y + dy };
    });
  }

  const base = mobile ? SUB_RING_RADIUS.mobile : SUB_RING_RADIUS.desktop;
  /*
   * Radius mit der Anzahl mitwachsen lassen. Auf einem Kreis liegen n Nodes
   * 2·r·sin(π/n) auseinander; bei acht Apps reicht der Basisradius dafür nicht
   * (der Test hat es gefunden: 10,8 statt 12). Die bindende Grösse ist der
   * kleinere Radius, deshalb wird an y gemessen und x proportional mitgezogen –
   * so bleibt die Ellipsenform erhalten.
   */
  const needed = MIN_SUB_DISTANCE / (2 * Math.sin(Math.PI / count));
  const scale = Math.max(1, needed / base.y);
  const radius = { x: base.x * scale, y: base.y * scale };

  return Array.from({ length: count }, (_, i) => {
    // Start unten (90°), damit der erste Node dort bleibt, wo er bei drei Apps
    // auch stand – der Wechsel auf den Ring soll nicht wie ein Sprung wirken.
    const angle = Math.PI / 2 + (i / count) * Math.PI * 2;
    return {
      x: Math.round((parent.x + Math.cos(angle) * radius.x) * 10) / 10,
      y: Math.round((parent.y + Math.sin(angle) * radius.y) * 10) / 10,
    };
  });
}

/** Kamerafahrt: Ziel-Transform, damit der Node bei 50 % / 40 % des Viewports landet. */
export const ZOOM_SCALE = 1.55;

export function cameraTransform(
  target: Point,
  size: { width: number; height: number },
  scale: number = ZOOM_SCALE,
): { x: number; y: number; scale: number } {
  const px = (target.x / 100) * size.width;
  const py = (target.y / 100) * size.height;
  return {
    x: size.width * 0.5 - scale * px,
    y: size.height * 0.4 - scale * py,
    scale,
  };
}

/** Initialen für den Avatar-Chip. */
export function initialsOf(nameOrEmail: string | null | undefined): string {
  if (!nameOrEmail) return '··';
  const source = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0]! : nameOrEmail;
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return '··';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
